/**
 * The attorney coverage RECORD — who the attorneys are and what each one covers.
 *
 * This is the system of record as of 2026-09-21, replacing the workbook Harrison Fritz and
 * Jill Wells maintained by hand. It is deliberately SEPARATE from `attorney_coverage`, which
 * is the (still empty, still flag-gated) routing projection: that table's state_code is
 * char(2) and cannot express the Texas and North Carolina regional splits, and its
 * attorney_id points at profiles -> auth.users, which would have meant 41 login accounts for
 * external attorneys.
 *
 * Authorization is server-side. Both tables are deny-by-default (RLS on, no policies, no
 * grant to anon/authenticated), so the browser reaches them only through these SECURITY
 * DEFINER RPCs: read is admin/superadmin/manager, write is admin/superadmin only.
 */

import { supabase } from "./supabase";

export interface AttorneyDirectoryRow {
  attorney_id: string;
  full_name: string;
  contact_email: string | null;
  firm_label: string | null;
  additional_emails: string[];
  attorney_active: boolean;
  attorney_needs_review: boolean;
  attorney_review_reason: string | null;
  state_code: string;
  /** NULL for a whole state; the workbook's verbatim wording for a sub-state area. */
  region: string | null;
  source_label: string;
  listed_position: number;
  listed_of: number;
  attorney_report_name: string | null;
  attorney_report_link: string | null;
  dealer_shield_report_name: string | null;
  dealer_shield_report_link: string | null;
  /** 'as-supplied' | 'repaired' — 27 of the workbook's URLs arrived truncated. */
  link_provenance: string | null;
  jurisdiction_needs_review: boolean;
  jurisdiction_review_reason: string | null;
  /** True when the workbook divides this state, so state alone cannot pick an attorney. */
  state_is_split: boolean;
}

export interface ResolvedAttorney {
  attorney_id: string;
  full_name: string;
  contact_email: string | null;
  state_code: string;
  sub_region: string;
  region: string | null;
  needs_review: boolean;
  /** 'state' | 'zip->county->territory' — how the member was placed. */
  resolution: string;
}

/** The 51 jurisdictions we expect to cover, so the page can name what is missing. */
export const ALL_JURISDICTIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
] as const;

function friendlyError(message: string): string {
  if (message.includes("not_authorized")) {
    return "You do not have permission to change the attorney record (admin or superadmin only).";
  }
  if (message.includes("invalid_state_code")) {
    return "Enter a two-letter state code, for example TX.";
  }
  if (message.includes("state_is_split_by_region")) {
    return "This state is recorded by region. Add the attorney to a region, not the whole state.";
  }
  if (message.includes("state_is_recorded_whole")) {
    return "This state has whole-state attorneys. Switch them off before splitting it by region.";
  }
  if (message.includes("region_and_code_must_agree")) {
    return "A sub-state area needs both a name and a routing code — give both, or leave both blank for a whole state.";
  }
  if (message.includes("review_reason_required")) {
    return "Say why this needs review; a flag with no reason cannot be acted on.";
  }
  if (message.includes("attorney_name_required")) {
    return "Enter the attorney's name.";
  }
  if (message.includes("attorney_not_found")) {
    return "That attorney is no longer on file — refresh and try again.";
  }
  if (message.includes("jurisdiction_not_found")) {
    return "That coverage row no longer exists — refresh and try again.";
  }
  if (message.includes("attorney_directory_name_uniq")) {
    return "An attorney with that name is already on file.";
  }
  if (message.includes("attorney_jurisdictions_unique_assignment")) {
    return "That attorney is already recorded for this state and area.";
  }
  if (message.includes("attorney_directory_email_shape")) {
    return "That does not look like an email address.";
  }
  return message;
}

export async function listAttorneyDirectory(
  includeInactive = false,
): Promise<AttorneyDirectoryRow[]> {
  const { data, error } = await supabase.rpc("list_attorney_directory", {
    p_include_inactive: includeInactive,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyDirectoryRow[];
}

/**
 * Who answers a question from this member?
 *
 * Reuses the SAME machinery that maps states and Texas counties to support regions, so both
 * systems agree on which half of Texas someone is in.
 *
 * ⚠️ An EMPTY array means "assign this by hand", NEVER "no attorney needed". It is returned
 * when the state has no cover at all, when a split state's ZIP cannot be placed, and when a
 * split state has no territory map (North Carolina today). The resolver fails closed rather
 * than guessing.
 */
export async function resolveAttorneyJurisdiction(
  state: string,
  postalCode?: string | null,
): Promise<ResolvedAttorney[]> {
  const { data, error } = await supabase.rpc("resolve_attorney_jurisdiction", {
    p_state: state,
    p_postal_code: postalCode ?? null,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as ResolvedAttorney[];
}

export async function upsertAttorneyDirectoryEntry(input: {
  id?: string | null;
  fullName: string;
  contactEmail?: string | null;
  firmLabel?: string | null;
  additionalEmails?: string[] | null;
  active?: boolean;
  needsReview?: boolean;
  reviewReason?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc(
    "upsert_attorney_directory_entry",
    {
      p_full_name: input.fullName,
      p_contact_email: input.contactEmail ?? null,
      p_firm_label: input.firmLabel ?? null,
      p_additional_emails: input.additionalEmails ?? null,
      p_active: input.active ?? true,
      p_needs_review: input.needsReview ?? false,
      p_review_reason: input.reviewReason ?? null,
      p_id: input.id ?? null,
    },
  );
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return data as string;
}

export async function upsertAttorneyJurisdiction(input: {
  id?: string | null;
  attorneyId: string;
  stateCode: string;
  subRegion?: string;
  region?: string | null;
  active?: boolean;
  needsReview?: boolean;
  reviewReason?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc("upsert_attorney_jurisdiction", {
    p_attorney_id: input.attorneyId,
    p_state_code: input.stateCode,
    p_sub_region: input.subRegion ?? "",
    p_region: input.region ?? null,
    p_active: input.active ?? true,
    p_needs_review: input.needsReview ?? false,
    p_review_reason: input.reviewReason ?? null,
    p_id: input.id ?? null,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return data as string;
}

export async function setAttorneyJurisdictionActive(
  id: string,
  active: boolean,
): Promise<{
  state_code: string;
  remaining_active: number;
  warning: string | null;
}> {
  const { data, error } = await supabase.rpc(
    "set_attorney_jurisdiction_active",
    {
      p_id: id,
      p_active: active,
    },
  );
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return data as {
    state_code: string;
    remaining_active: number;
    warning: string | null;
  };
}

/** Jurisdictions with nobody on file. Named, not merely absent — 97 active members live there. */
export function uncoveredJurisdictions(rows: AttorneyDirectoryRow[]): string[] {
  const covered = new Set(
    rows.filter((r) => r.attorney_active).map((r) => r.state_code),
  );
  return ALL_JURISDICTIONS.filter((s) => !covered.has(s));
}

/** One row per state for the summary table, collapsing co-counsel and regions. */
export interface StateSummary {
  state_code: string;
  is_split: boolean;
  areas: { region: string | null; attorneys: AttorneyDirectoryRow[] }[];
  needs_review: boolean;
}

export function summariseByState(rows: AttorneyDirectoryRow[]): StateSummary[] {
  const byState = new Map<string, AttorneyDirectoryRow[]>();
  for (const r of rows) {
    const list = byState.get(r.state_code) ?? [];
    list.push(r);
    byState.set(r.state_code, list);
  }
  return [...byState.entries()]
    .map(([state_code, list]) => {
      const areas = new Map<string, AttorneyDirectoryRow[]>();
      for (const r of list) {
        const key = r.region ?? "";
        const a = areas.get(key) ?? [];
        a.push(r);
        areas.set(key, a);
      }
      return {
        state_code,
        is_split: list.some((r) => r.state_is_split),
        areas: [...areas.entries()]
          .map(([region, attorneys]) => ({ region: region || null, attorneys }))
          .sort((a, b) => (a.region ?? "").localeCompare(b.region ?? "")),
        needs_review: list.some(
          (r) => r.attorney_needs_review || r.jurisdiction_needs_review,
        ),
      };
    })
    .sort((a, b) => a.state_code.localeCompare(b.state_code));
}
